import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import {
  DEFAULT_UNAUTHORIZED_MODE,
  NO_ACCESS_REDIRECT_PATH,
  UNAUTHORIZED_MODES,
} from '../constants/permissionMode';
import { validateApiResponse } from '../../../services';
import { getActorPermissions } from '../../../services/permissionsService';
import { hasPermission } from '../utils/hasPermission';

export const PermissionsContext = createContext(null);

const MODE_VALUES = Object.values(UNAUTHORIZED_MODES);

const resolveMode = (override) =>
  MODE_VALUES.includes(override) ? override : DEFAULT_UNAUTHORIZED_MODE;

/**
 * Loads the signed-in actor once and exposes `{ loading, check, actor, mode, noAccessRedirectPath }`
 * (`mode` is the default hide/lock rule for callers that filter a list themselves). While loading, and if the
 * request fails, nothing is granted (and a failure raises an error toast).
 *
 * @param {object} props
 * @param {() => Promise<object>} [props.loadActor] Injectable loader (defaults to the mock service).
 * @param {string} [props.noAccessRedirectPath] Where a viewer who may not see a route is sent in
 *   `hide` mode. Supplied by the host (the shell home); defaults to `/`.
 */
const PermissionsProvider = ({
  children,
  loadActor = getActorPermissions,
  noAccessRedirectPath = NO_ACCESS_REDIRECT_PATH,
}) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const [state, setState] = useState({ loading: true, actor: null });
  // Latest translator / toast without re-running the load when their identity changes.
  const tRef = useRef(t);
  const toastRef = useRef(toast);

  useEffect(() => {
    tRef.current = t;
    toastRef.current = toast;
  });

  useEffect(() => {
    let mounted = true;

    loadActor()
      .then((res) => validateApiResponse(res, tRef.current('guard.loadError')))
      .then(({ body }) => {
        if (mounted) setState({ loading: false, actor: body?.actor ?? null });
      })
      .catch((err) => {
        if (!mounted) return;
        setState({ loading: false, actor: null });
        toastRef.current.error({
          title: tRef.current('guard.loadError'),
          message: err?.message,
          canClose: true,
        });
      });

    return () => {
      mounted = false;
    };
  }, [loadActor]);

  const { loading, actor } = state;

  const check = useCallback(
    (key, options) => ({
      allowed: !loading && hasPermission(actor, key),
      loading,
      mode: resolveMode(options?.mode),
    }),
    [loading, actor],
  );

  const value = useMemo(
    () => ({ loading, check, actor, mode: DEFAULT_UNAUTHORIZED_MODE, noAccessRedirectPath }),
    [loading, check, actor, noAccessRedirectPath],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export default PermissionsProvider;
